import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { HtmlEditor } from "components/editors/HtmlEditor"
import { Modal } from "components/modals/Modal"

import { CMS_LOCALE_NAMES, SEO_DESCRIPTION_LIMIT, SEO_TITLE_LIMIT } from "services/cms-seo/type"
import { seoService } from "services/seo/seo.service"
import { SEO_KNOWN_PATHS, SeoOverride, SeoOverrideInput } from "services/seo/type"

import { normalisePath } from "./helpers/format"
import { Counter, Field, FieldRow, ghostButtonClass, inputClass, LocaleChipBar, modalBoxClass, primaryButtonClass, SectionHeading, ToggleRow } from "./CmsUi"

export type RouteSeoState = { kind: "none" } | { kind: "create" } | { kind: "edit"; path: string; locale?: string }

const FALLBACK_LOCALE = "en"

const emptyRow = (path: string, locale: string): SeoOverrideInput => ({
  locale,
  path,
  title: null,
  description: null,
  h1: null,
  canonical: null,
  robots_index: true,
  robots_follow: true,
  robots_advanced: null,
  og_title: null,
  og_description: null,
  og_image: null,
  footer_html: "",
  footer_collapsed: true,
  json_ld: [],
  active: true
})

const toInput = (row: SeoOverride): SeoOverrideInput => {
  const { id: _id, tenant_id: _tenant, created_at: _created, updated_at: _updated, ...rest } = row
  return rest
}

/** English where it exists, since that is what the others fall back to. */
const firstEditableLocale = (rows: SeoOverride[]): string => {
  if (rows.some((row) => row.locale === FALLBACK_LOCALE)) return FALLBACK_LOCALE
  return rows[0]?.locale ?? FALLBACK_LOCALE
}

interface RouteSeoModalProps {
  state: RouteSeoState
  onClose: () => void
  onSaved: () => void
}

/**
 * A route is the same path in all languages. One without a row of its own starts
 * from the English copy, which is how the site resolves it, and is created on save.
 */
export const RouteSeoModal: FC<RouteSeoModalProps> = ({ state, onClose, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [path, setPath] = useState("")
  const [rows, setRows] = useState<SeoOverride[]>([])
  const [locale, setLocale] = useState(FALLBACK_LOCALE)
  const [form, setForm] = useState<SeoOverrideInput>(emptyRow("", FALLBACK_LOCALE))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const isCreate = state.kind === "create"
  const existing = rows.find((row) => row.locale === locale) ?? null

  const load = useCallback(async () => {
    if (state.kind !== "edit") return

    setLoading(true)
    const items = (await seoService.listOverridesForPath(state.path)) ?? []
    setRows(items)
    setPath(state.path)

    setLocale(state.locale ?? firstEditableLocale(items))
    setLoading(false)
  }, [state])

  useEffect(() => {
    if (state.kind === "create") {
      setRows([])
      setPath("")
      setLocale(FALLBACK_LOCALE)
      setForm(emptyRow("", FALLBACK_LOCALE))
      return
    }
    load()
  }, [state, load])

  // Starts from what the site is serving, not from blank fields.
  useEffect(() => {
    const own = rows.find((row) => row.locale === locale)
    if (own) {
      setForm(toInput(own))
      return
    }

    const fallback = rows.find((row) => row.locale === FALLBACK_LOCALE)
    setForm(fallback ? { ...toInput(fallback), locale } : emptyRow(path, locale))
  }, [rows, locale, path])

  const set = <K extends keyof SeoOverrideInput>(key: K, value: SeoOverrideInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const text = (key: keyof SeoOverrideInput) => (form[key] as string | null) ?? ""

  // A row overriding nothing resolves to the brand defaults.
  const hasContent = Boolean(
    form.title ||
      form.description ||
      form.h1 ||
      form.canonical ||
      form.og_title ||
      form.og_description ||
      form.og_image ||
      form.footer_html?.trim()
  )
  const effectivePath = normalisePath(state.kind === "edit" ? state.path : path)
  const canSave = !saving && (hasContent || !form.robots_index)

  const submit = async () => {
    if (!canSave) return

    setSaving(true)
    const payload: SeoOverrideInput = { ...form, path: effectivePath === "/" ? "" : effectivePath, locale }
    const saved = existing
      ? await seoService.updateOverride(existing.id, payload)
      : await seoService.createOverride(payload)
    setSaving(false)

    if (!saved) return

    toast.success(
      t("seo.savedToast", {
        defaultValue: "{{name}} saved",
        name: CMS_LOCALE_NAMES[locale] ?? locale
      }) as string
    )
    setRows((prev) => [...prev.filter((row) => row.locale !== locale), saved])
    onSaved()
  }

  const removeLanguage = async () => {
    if (!existing) return
    if (!(await seoService.deleteOverride(existing.id))) return

    toast.success(t("seo.languageDeleted", { defaultValue: "Language removed" }) as string)
    setRows((prev) => prev.filter((row) => row.locale !== existing.locale))
    onSaved()
  }

  return (
    <Modal open={state.kind !== "none"} handler={onClose}>
      <div className={`${modalBoxClass} max-w-2xl`}>
        <h3 className="mb-4 text-lg font-semibold">
          {isCreate
            ? t("seo.newRoute", { defaultValue: "Add a route override" })
            : t("seo.editRoute", { defaultValue: "Route SEO" })}
        </h3>

        <FieldRow>
          <Field
            label={t("seo.col.path", { defaultValue: "Page path" })}
            hint={t("seo.pathHint", {
              defaultValue: "Locale-less. Empty means the home page. Stored lowercase, no trailing slash."
            })}
          >
            {isCreate ? (
              <input
                className={`${inputClass} font-mono`}
                value={path}
                list="seo-known-paths"
                placeholder="/terms-conditions"
                onChange={(event) => setPath(event.target.value)}
                onBlur={(event) => setPath(normalisePath(event.target.value))}
              />
            ) : (
              <div className={`${inputClass} flex items-center font-mono opacity-70`}>{effectivePath}</div>
            )}
            <datalist id="seo-known-paths">
              {SEO_KNOWN_PATHS.map((known) => (
                <option key={known} value={known || "/"} />
              ))}
            </datalist>
          </Field>
        </FieldRow>

        <div className="mb-5">
          <LocaleChipBar
            current={locale}
            present={rows.map((row) => row.locale)}
            onSelect={setLocale}
            onCreate={isCreate ? undefined : setLocale}
          />
        </div>

        {loading ? (
          <p className="py-8 text-center text-sm">{t("common.loading", { defaultValue: "Loading..." })}</p>
        ) : (
          <>
            {!existing && !isCreate && (
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                {t("seo.inheritedNotice", {
                  defaultValue:
                    "{{name}} has no override of its own and currently serves the English one. Saving creates it.",
                  name: CMS_LOCALE_NAMES[locale] ?? locale
                })}
              </p>
            )}

            <FieldRow>
              <Field
                label={t("seo.col.title", { defaultValue: "Title" })}
                counter={<Counter length={text("title").length} limit={SEO_TITLE_LIMIT} />}
              >
                <input
                  className={inputClass}
                  value={text("title")}
                  onChange={(event) => set("title", event.target.value || null)}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field
                label={t("seo.metaDescription", { defaultValue: "Meta description" })}
                counter={<Counter length={text("description").length} limit={SEO_DESCRIPTION_LIMIT} />}
              >
                <textarea
                  className={inputClass}
                  rows={3}
                  value={text("description")}
                  onChange={(event) => set("description", event.target.value || null)}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field
                label="H1"
                hint={t("seo.h1Hint", {
                  defaultValue: "Only applied on routes that render the shared page heading; the policy pages keep their own."
                })}
              >
                <input className={inputClass} value={text("h1")} onChange={(event) => set("h1", event.target.value || null)} />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field
                label={t("seo.canonical", { defaultValue: "Canonical URL" })}
                hint={t("seo.canonicalHint", { defaultValue: "Leave empty and the route canonicalises to itself." })}
              >
                <input
                  className={inputClass}
                  value={text("canonical")}
                  onChange={(event) => set("canonical", event.target.value || null)}
                />
              </Field>
            </FieldRow>

            <ToggleRow
              title={t("cmsSeo.field.index", { defaultValue: "Allow search engines to index" })}
              checked={form.robots_index}
              onChange={() => set("robots_index", !form.robots_index)}
            />
            <ToggleRow
              title={t("cmsSeo.field.follow", { defaultValue: "Allow following links" })}
              checked={form.robots_follow}
              onChange={() => set("robots_follow", !form.robots_follow)}
            />
            <SectionHeading>{t("cmsSeo.footerCopy", { defaultValue: "SEO copy below the page" })}</SectionHeading>

            <p className="mb-3 text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
              {t("cmsSeo.footerCopyHint", {
                defaultValue:
                  "Rendered under the page's own content. The text is in the page source whichever state it starts in, so search engines read it either way — collapsing only keeps it out of a player's way."
              })}
            </p>

            <HtmlEditor value={form.footer_html ?? ""} onChange={(html) => set("footer_html", html)} minHeight={140} />

            <div className="mb-2 mt-3">
              <ToggleRow
                title={t("cmsSeo.footerCollapsed", { defaultValue: "Start collapsed" })}
                checked={form.footer_collapsed}
                onChange={() => set("footer_collapsed", !form.footer_collapsed)}
              />
            </div>

            <ToggleRow
              title={t("seo.active", { defaultValue: "Active" })}
              description={t("seo.activeHint", { defaultValue: "Off leaves the row in place but serves the defaults" })}
              checked={form.active}
              onChange={() => set("active", !form.active)}
            />
          </>
        )}

        <div className="modal-action flex-wrap">
          {existing && (
            <button type="button" className="btn btn-sm btn-ghost text-rose-500 normal-case mr-auto" onClick={removeLanguage}>
              {t("seo.deleteLanguage", {
                defaultValue: "Delete {{name}}",
                name: (locale ?? "").toUpperCase()
              })}
            </button>
          )}
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.close", { defaultValue: "Close" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={!canSave} onClick={submit}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
